import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LuckyWheel } from "@stellex-tycoon/lucky-wheel-ui";
import type { WheelTier, WheelTierName, SpinResult } from "@stellex-tycoon/shared-types";
import { fetchTiers, spinWheel, fetchSpinResult } from "../api/wheel.api";
import { fetchBalance } from "../api/auth.api";

export function WheelScene({ onReward }: { onReward?: () => void }) {
  const [tiers, setTiers] = useState<WheelTier[]>([]);
  const [activeTier, setActiveTier] = useState<WheelTierName>("BRONZE");
  const [balance, setBalance] = useState<bigint>(0n);
  const [spinning, setSpinning] = useState(false);
  const [outcome, setOutcome] = useState<SpinResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [t, b] = await Promise.all([fetchTiers(), fetchBalance()]);
      setTiers(t);
      setBalance(BigInt(b.balance));
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? "failed to load wheel");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const tier = tiers.find((t) => t.name === activeTier);
  const entryCost = tier ? BigInt(tier.entryCostTokens) : 0n;
  const canAfford = balance >= entryCost;

  const handleSpin = async () => {
    if (!tier || spinning) return;
    setSpinning(true);
    setError(null);
    setOutcome(null);
    try {
      const { spinId } = await spinWheel(activeTier);
      // Poll the result — usually resolved synchronously in MVP.
      let attempts = 0;
      const poll = async (): Promise<SpinResult | null> => {
        attempts++;
        const result = await fetchSpinResult(spinId);
        if (result.status !== "PENDING") return result;
        if (attempts > 20) return null;
        await new Promise((r) => setTimeout(r, 500));
        return poll();
      };
      const result = await poll();
      if (result) setOutcome(result);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? "spin failed");
      setSpinning(false);
    }
  };

  const handleSpinComplete = () => {
    setSpinning(false);
    load(); // refresh balance after spin (debit + any token-refund credit)
    onReward?.();
  };

  if (!tier) {
    return (
      <section className="wheel-scene">
        {error ? <div className="error-banner">{error}</div> : <div className="loading">Loading wheel…</div>}
      </section>
    );
  }

  return (
    <section className="wheel-scene">
      <div className="tier-tabs">
        {tiers.map((t) => (
          <button
            key={t.name}
            className={`tier-tab ${t.name === activeTier ? "active" : ""}`}
            onClick={() => setActiveTier(t.name)}
            disabled={spinning}
            type="button"
          >
            {t.name}
            <span className="tier-cost">{Number(t.entryCostTokens).toLocaleString()}</span>
          </button>
        ))}
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="wheel-area">
        <LuckyWheel
          tier={tier}
          outcome={outcome ?? undefined}
          onSpinComplete={handleSpinComplete}
          width={300}
        />
      </div>

      <div className="spin-controls">
        <button
          className="spin-button"
          disabled={!canAfford || spinning}
          onClick={handleSpin}
          type="button"
        >
          {spinning ? "Spinning…" : `Spin · ${Number(entryCost).toLocaleString()} tokens`}
        </button>
        {!canAfford && !spinning && (
          <p className="muted spin-hint">
            Need {Number(entryCost - balance).toLocaleString()} more tokens
          </p>
        )}
      </div>

      <AnimatePresence>
        {outcome && outcome.status === "RESOLVED" && !spinning && (
          <motion.div
            className="outcome-card"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
          >
            {outcome.prize ? (
              <>
                <div className="outcome-emoji">🎉</div>
                <h3>You won!</h3>
                <p className="outcome-prize">{outcome.prize.name}</p>
              </>
            ) : (
              <>
                <div className="outcome-emoji">😅</div>
                <h3>Better luck next spin</h3>
                <p className="muted">No prize this round.</p>
              </>
            )}
            <details className="provably-fair">
              <summary>Provably fair</summary>
              <p>VRF: <code>{outcome.vrfRandomValue.slice(0, 16)}…</code></p>
              <p>Proof: <code>{outcome.vrfProof.slice(0, 16)}…</code></p>
            </details>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="prize-list">
        <h3>Possible Prizes</h3>
        <ul>
          {tier.prizes.map((p) => (
            <li key={p.prizeId} className={`rarity-${p.rarityHint}`}>
              <span className="prize-name">{p.name}</span>
              <span className="prize-rarity">{p.rarityHint}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
